/**
 * The outbox: writes a field officer made with no connection, kept in
 * IndexedDB until the server has actually accepted them.
 *
 * Nothing in here pretends a queued write succeeded, and nothing is ever
 * dropped because it failed — a refused item stays, with the reason.
 *
 * Bundled into the service worker as well, so: no `window`, no `document`.
 */

import {
  STORE_BLOBS,
  STORE_OUTBOX,
  idbDelete,
  idbGet,
  idbGetAll,
  idbPut,
  metaGet,
  metaSet,
} from './db';
import type { OutboxError, OutboxItem, OutboxKind, OutboxStatus, SyncOutcome } from './types';

/** Waits between attempts: 5s, 15s, 45s, 2min, 5min, 15min. */
export const BACKOFF_MS = [5_000, 15_000, 45_000, 120_000, 300_000, 900_000];

/** After this many failed tries the item asks for a person. */
export const MAX_ATTEMPTS = 8;

const LOCK_KEY = 'sync-lock';
const LOCK_TTL_MS = 60_000;
const LAST_SYNC_KEY = 'last-sync-at';
export const CHANNEL_NAME = 'staketory-offline';

export interface StoredBlob {
  key: string;
  blob: Blob;
  name: string;
  type: string;
  size: number;
  createdAt: number;
}

export interface TransportRequest {
  method: string;
  path: string;
  query?: Record<string, string>;
  body?: Record<string, unknown>;
  file?: { blob: Blob; name: string };
  idempotencyKey: string;
}

export interface TransportResponse {
  ok: boolean;
  /** 0 means the request never reached the server */
  status: number;
  data?: any;
  message?: string;
}

export type Transport = (request: TransportRequest) => Promise<TransportResponse>;

export interface EnqueueInput {
  kind: OutboxKind;
  label: string;
  detail?: string;
  method: 'POST' | 'PUT' | 'PATCH';
  path: string;
  body?: Record<string, unknown>;
  query?: Record<string, string>;
  file?: { blob: Blob; name: string };
  refs?: OutboxItem['refs'];
  dependsOn?: string;
  userId?: string;
  idempotencyKey?: string;
}

/** RFC4122-ish id; `crypto.randomUUID` where the browser has it. */
export function newId(): string {
  const cryptoObj: Crypto | undefined =
    typeof crypto !== 'undefined' ? (crypto as Crypto) : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    /* eslint-disable no-bitwise */
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    /* eslint-enable no-bitwise */
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

let notify: (() => void) | null = null;

/** The page passes a callback so the queue badge updates as things move. */
export function onOutboxChanged(listener: (() => void) | null): void {
  notify = listener;
}

let channel: BroadcastChannel | null = null;
function broadcast(): void {
  notify?.();
  try {
    if (typeof BroadcastChannel === 'undefined') return;
    if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage({ type: 'outbox-changed' });
  } catch {
    /* a broadcast is a nicety, never a requirement */
  }
}

export async function putBlob(blob: Blob, name: string): Promise<string> {
  const key = `blob-${newId()}`;
  const stored: StoredBlob = {
    key,
    blob,
    name,
    type: blob.type,
    size: blob.size,
    createdAt: Date.now(),
  };
  await idbPut(STORE_BLOBS, stored);
  return key;
}

export const getBlob = (key: string): Promise<StoredBlob | undefined> =>
  idbGet<StoredBlob>(STORE_BLOBS, key);

export const deleteBlob = (key: string): Promise<void> => idbDelete(STORE_BLOBS, key);

/** Queue a write. Returns the item so the caller can show it as pending. */
export async function enqueue(input: EnqueueInput, now: number = Date.now()): Promise<OutboxItem> {
  const blobKey = input.file ? await putBlob(input.file.blob, input.file.name) : undefined;
  const item: OutboxItem = {
    id: newId(),
    kind: input.kind,
    idempotencyKey: input.idempotencyKey ?? newId(),
    label: input.label,
    detail: input.detail,
    method: input.method,
    path: input.path,
    body: input.body,
    query: input.query,
    blobKey,
    fileName: input.file?.name,
    refs: input.refs,
    dependsOn: input.dependsOn,
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    status: 'pending',
    userId: input.userId,
  };
  await idbPut(STORE_OUTBOX, item);
  broadcast();
  return item;
}

/** Oldest first — the order the officer made them, and the order they sync. */
export async function listItems(): Promise<OutboxItem[]> {
  const items = await idbGetAll<OutboxItem>(STORE_OUTBOX);
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

export const getItem = (id: string): Promise<OutboxItem | undefined> =>
  idbGet<OutboxItem>(STORE_OUTBOX, id);

export async function saveItem(item: OutboxItem): Promise<void> {
  await idbPut(STORE_OUTBOX, { ...item, updatedAt: Date.now() });
  broadcast();
}

/**
 * Throw an item away at the officer's request. Anything that depended on it
 * (a card photo for a person who is no longer being created) goes too —
 * the caller is told how many, so the UI can say so.
 */
export async function discardItem(id: string): Promise<number> {
  const items = await listItems();
  const doomed = [id, ...items.filter((item) => item.dependsOn === id).map((item) => item.id)];
  for (const victimId of doomed) {
    const item = items.find((candidate) => candidate.id === victimId);
    if (item?.blobKey) await deleteBlob(item.blobKey);
    await idbDelete(STORE_OUTBOX, victimId);
  }
  broadcast();
  return doomed.length;
}

/** Put a refused or waiting item back at the front of the line. */
export async function retryItem(id: string): Promise<OutboxItem | undefined> {
  const item = await getItem(id);
  if (!item) return undefined;
  const next: OutboxItem = {
    ...item,
    status: 'pending',
    error: undefined,
    attempts: 0,
    nextAttemptAt: undefined,
    updatedAt: Date.now(),
  };
  await idbPut(STORE_OUTBOX, next);
  broadcast();
  return next;
}

/** Amend a blocked item (fix a validation error) and queue it again. */
export async function amendItem(
  id: string,
  body: Record<string, unknown>,
): Promise<OutboxItem | undefined> {
  const item = await getItem(id);
  if (!item) return undefined;
  const next: OutboxItem = {
    ...item,
    body: { ...(item.body ?? {}), ...body },
    // a changed body needs a new key, or the server replays the old outcome
    idempotencyKey: newId(),
    status: 'pending',
    error: undefined,
    attempts: 0,
    nextAttemptAt: undefined,
    updatedAt: Date.now(),
  };
  await idbPut(STORE_OUTBOX, next);
  broadcast();
  return next;
}

export const backoffFor = (attempts: number): number =>
  BACKOFF_MS[Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1];

/** One line, in plain words, about why the server said no. */
export function describeFailure(status: number, message?: string): OutboxError {
  const at = Date.now();
  if (status === 0) {
    return { status, at, retryable: true, message: 'No connection — still waiting' };
  }
  if (status === 401 || status === 403) {
    return {
      status,
      at,
      retryable: false,
      message: 'Sign in again to send this — your session expired',
    };
  }
  if (status === 404) {
    return {
      status,
      at,
      retryable: false,
      message: 'That record no longer exists on the server',
    };
  }
  if (status === 409) {
    return {
      status,
      at,
      retryable: false,
      message: message || 'Someone changed this record before your edit arrived',
    };
  }
  if (status === 413) {
    return { status, at, retryable: false, message: 'The photo is too large to upload' };
  }
  if (status === 400 || status === 422) {
    return { status, at, retryable: false, message: message || 'The server rejected these details' };
  }
  if (status === 429 || status >= 500) {
    return { status, at, retryable: true, message: message || 'The server is busy — will try again' };
  }
  return { status, at, retryable: false, message: message || `Request failed (${status})` };
}

const statusAfterFailure = (error: OutboxError, attempts: number): OutboxStatus =>
  error.retryable && attempts < MAX_ATTEMPTS ? 'pending' : 'blocked';

/**
 * Fill in values that only existed once an earlier item synced — the id of a
 * stakeholder created offline, for the card photo that belongs to them.
 */
async function resolveReferences(localId: string, serverId: string): Promise<void> {
  const items = await listItems();
  for (const item of items) {
    if (!item.refs) continue;
    let changed = false;
    const next: OutboxItem = { ...item };

    if (item.refs.body) {
      for (const [field, ref] of Object.entries(item.refs.body)) {
        if (ref !== localId) continue;
        next.body = { ...(next.body ?? {}), [field]: serverId };
        changed = true;
      }
    }
    if (item.refs.query) {
      for (const [field, ref] of Object.entries(item.refs.query)) {
        if (ref !== localId) continue;
        next.query = { ...(next.query ?? {}), [field]: serverId };
        changed = true;
      }
    }
    if (item.refs.path === localId) {
      next.path = item.path.replace(`:${localId}`, serverId).replace(localId, serverId);
      changed = true;
    }

    if (changed) {
      if (next.dependsOn === localId) next.dependsOn = undefined;
      next.updatedAt = Date.now();
      await idbPut(STORE_OUTBOX, next);
    }
  }
}

/** A single writer at a time: the page and the service worker share this. */
async function acquireLock(now: number): Promise<boolean> {
  const held = await metaGet<number>(LOCK_KEY);
  if (held && now - held < LOCK_TTL_MS) return false;
  await metaSet(LOCK_KEY, now);
  return true;
}

const releaseLock = () => metaSet(LOCK_KEY, 0);

export const lastSyncAt = (): Promise<number | undefined> => metaGet<number>(LAST_SYNC_KEY);

export interface ProcessOptions {
  transport: Transport;
  now?: number;
  /** only send this officer's queue */
  userId?: string;
  /** ignore the shared lock (tests) */
  force?: boolean;
}

/**
 * Send what is due, oldest first, stopping as soon as the connection is gone
 * again so the rest of the queue keeps its place in line.
 */
export async function processOutbox(options: ProcessOptions): Promise<SyncOutcome> {
  const { transport, userId } = options;
  const now = options.now ?? Date.now();

  if (!options.force && !(await acquireLock(now))) {
    const waiting = (await listItems()).filter((item) => item.status !== 'done');
    return { sent: 0, blocked: 0, remaining: waiting.length };
  }

  let sent = 0;
  let blocked = 0;
  const skipped = new Set<string>();

  try {
    const queue = await listItems();
    for (const queued of queue) {
      // re-read: an earlier item in this run may have filled in an id for it
      const item = (await getItem(queued.id)) ?? queued;
      if (userId && item.userId && item.userId !== userId) continue;
      if (item.status === 'blocked') {
        skipped.add(item.id);
        blocked += 1;
        continue;
      }
      if (item.dependsOn && skipped.has(item.dependsOn)) {
        skipped.add(item.id);
        continue;
      }
      if (item.nextAttemptAt && item.nextAttemptAt > now) {
        skipped.add(item.id);
        continue;
      }

      const attempts = item.attempts + 1;
      await idbPut(STORE_OUTBOX, {
        ...item,
        status: 'syncing' as OutboxStatus,
        attempts,
        lastAttemptAt: now,
        updatedAt: now,
      });
      broadcast();

      let response: TransportResponse;
      try {
        const file = item.blobKey ? await getBlob(item.blobKey) : undefined;
        response = await transport({
          method: item.method,
          path: item.path,
          query: item.query,
          body: item.body,
          file: file ? { blob: file.blob, name: file.name } : undefined,
          idempotencyKey: item.idempotencyKey,
        });
      } catch (error: any) {
        response = { ok: false, status: 0, message: error?.message };
      }

      if (response.ok) {
        const serverId = (response.data as any)?.id;
        if (typeof serverId === 'string') {
          await resolveReferences(item.id, serverId);
        }
        if (item.blobKey) await deleteBlob(item.blobKey);
        await idbDelete(STORE_OUTBOX, item.id);
        sent += 1;
        broadcast();
        continue;
      }

      const error = describeFailure(response.status, response.message);
      const status = statusAfterFailure(error, attempts);
      await idbPut(STORE_OUTBOX, {
        ...item,
        status,
        attempts,
        lastAttemptAt: now,
        nextAttemptAt: status === 'pending' ? now + backoffFor(attempts) : undefined,
        error,
        updatedAt: now,
      });
      skipped.add(item.id);
      if (status === 'blocked') blocked += 1;
      broadcast();

      // The connection went away again: leave the rest of the queue untouched.
      if (response.status === 0) break;
    }

    await metaSet(LAST_SYNC_KEY, now);
  } finally {
    if (!options.force) await releaseLock();
  }

  const remaining = (await listItems()).filter((item) => item.status !== 'done').length;
  return { sent, blocked, remaining };
}

/** Wipe the queue. Only used when a different officer signs in on the phone. */
export async function clearOutbox(): Promise<void> {
  const items = await listItems();
  for (const item of items) {
    if (item.blobKey) await deleteBlob(item.blobKey);
    await idbDelete(STORE_OUTBOX, item.id);
  }
  broadcast();
}
